import { Button } from '@/shared/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/shared/ui/card';
import { Copy } from 'lucide-react';
import { Unit, ImageDimensions, PhotoMarker, Position } from './types/types';
import { useEffect, useState, useCallback } from 'react';
import { trackEvent } from '@/shared/utils/google-analytics';

// Utility function to calculate polygon centroid (visual center)
const calculatePolygonCentroid = (points: number[][]): { x: number; y: number } => {
  if (points.length < 3) {
    // Fallback for invalid polygons - use geometric center
    return {
      x: points.reduce((sum, p) => sum + p[0], 0) / points.length,
      y: points.reduce((sum, p) => sum + p[1], 0) / points.length,
    };
  }

  let area = 0;
  let centroidX = 0;
  let centroidY = 0;

  // Calculate polygon area and centroid using shoelace formula
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    const xi = points[i][0];
    const yi = points[i][1];
    const xj = points[j][0];
    const yj = points[j][1];

    const a = xi * yj - xj * yi;
    area += a;
    centroidX += (xi + xj) * a;
    centroidY += (yi + yj) * a;
  }

  area *= 0.5;

  // Handle degenerate polygons (area near zero)
  if (Math.abs(area) < 0.001) {
    // Fallback to geometric center
    return {
      x: points.reduce((sum, p) => sum + p[0], 0) / points.length,
      y: points.reduce((sum, p) => sum + p[1], 0) / points.length,
    };
  }

  return {
    x: centroidX / (6 * area),
    y: centroidY / (6 * area),
  };
};

const normalizePolygonPoints = (points: number[][]): number[][] => {
  if (!Array.isArray(points) || points.length === 0) return [];
  const normalized = points
    .filter((p) => Array.isArray(p) && p.length === 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))
    .map((p) => [p[0], p[1]]);
  if (normalized.length > 1) {
    const [fx, fy] = normalized[0];
    const [lx, ly] = normalized[normalized.length - 1];
    if (fx === lx && fy === ly) {
      return normalized.slice(0, -1);
    }
  }
  return normalized;
};

const wrapTextLines = (text: string, maxCharsPerLine: number, maxLines: number): string[] => {
  const source = (text || '').trim();
  if (!source) return [''];
  const safeMaxChars = Math.max(4, maxCharsPerLine);
  const words = source.split(/\s+/);
  const lines: string[] = [];
  let current = '';

  const pushCurrent = () => {
    if (current.trim()) lines.push(current.trim());
    current = '';
  };

  for (const word of words) {
    if (word.length > safeMaxChars) {
      if (current) pushCurrent();
      let start = 0;
      while (start < word.length) {
        lines.push(word.slice(start, start + safeMaxChars));
        start += safeMaxChars;
      }
      continue;
    }
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= safeMaxChars) {
      current = candidate;
    } else {
      pushCurrent();
      current = word;
    }
  }
  pushCurrent();

  if (lines.length <= maxLines) return lines;
  const sliced = lines.slice(0, maxLines);
  const last = sliced[maxLines - 1];
  sliced[maxLines - 1] = last.length > 1 ? `${last.slice(0, Math.max(1, last.length - 1))}\u2026` : '\u2026';
  return sliced;
};

interface MainContentAreaProps {
  getCursor: () => string;
  imageDimensions: ImageDimensions;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleSvgClick: (e: React.MouseEvent) => void;
  selectedLayoutId: string;
  units: Unit[];
  photoMarkers: PhotoMarker[];
  svgRef: React.RefObject<SVGSVGElement>;
  groupRef: React.RefObject<SVGGElement>;
  position: Position;
  scale: number;
  imagePath: string;
  selectedUnit: Unit | null;
  currentPolygon: number[][];
  selectedMarkers: Set<number>;
  markersInAreas: Set<PhotoMarker>;
  areaPhotoMap: Map<number, number[]>;
  photoIndexMode: boolean;
  showCoordinates: boolean;
  handleUnitClick: (unit: Unit, event: React.MouseEvent) => void;
  handleMarkerClick: (marker: PhotoMarker, event: React.MouseEvent) => void;
  deletePhotoMarker: (markerId: number) => void;
  handleMarkerRightClick?: (event: React.MouseEvent) => void;
  handleMarkerDragStart?: (markerId: number, event: React.MouseEvent) => void;
  onWheelZoom?: (e: React.WheelEvent) => void;
  handleTouchStart?: (e: React.TouchEvent) => void;
  handleTouchMove?: (e: React.TouchEvent) => void;
  handleTouchEnd?: (e: React.TouchEvent) => void;
  disableMarkerClicks?: boolean;
  textEditingMode?: boolean;
  onRequestLabelEdit?: (areaId: number) => void;
  pendingTextChanges?: Map<number, Partial<{ name: string; text_position: [number, number]; text_size: number; text_rotation: number }>>;
  onSaveTextChanges?: () => void;
  onCancelTextChanges?: () => void;
  polygonEditingMode?: boolean;
  onPolygonUpdate?: (areaId: number, newCoordinates: number[][]) => void;
  pendingPolygonChanges?: Map<number, number[][]>;
  showAreaDeleteControls?: boolean;
  onDeleteAreaTag?: (areaId: number) => void;
}

const MainContentArea = ({
  getCursor,
  imageDimensions,
  handleMouseDown,
  handleSvgClick,
  selectedLayoutId,
  units,
  photoMarkers,
  svgRef,
  groupRef,
  position,
  scale,
  imagePath,
  selectedUnit,
  currentPolygon,
  selectedMarkers,
  markersInAreas,
  areaPhotoMap,
  photoIndexMode,
  showCoordinates,
  handleUnitClick,
  handleMarkerClick,
  deletePhotoMarker,
  handleMarkerRightClick,
  handleMarkerDragStart,
  onWheelZoom,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  disableMarkerClicks = false,
  textEditingMode = false,
  onRequestLabelEdit,
  pendingTextChanges = new Map(),
  onSaveTextChanges,
  onCancelTextChanges,
  polygonEditingMode = false,
  onPolygonUpdate,
  pendingPolygonChanges = new Map(),
  showAreaDeleteControls = false,
  onDeleteAreaTag,
}: MainContentAreaProps) => {
  const [editingPolygonId, setEditingPolygonId] = useState<number | null>(null);
  const [draggingNodeIndex, setDraggingNodeIndex] = useState<number | null>(null);
  const [snapPosition, setSnapPosition] = useState<{ x: number; y: number } | null>(null);
  const [showSnapIndicator, setShowSnapIndicator] = useState(false);

  // Get SVG coordinates from mouse event (accounting for the group transform)
  const getSVGCoordinates = useCallback((event: React.MouseEvent | MouseEvent): { x: number; y: number } => {
    if (!svgRef.current) return { x: 0, y: 0 };

    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    
    // Get the current viewBox (which changes with zoom/pan)
    const viewBox = svg.viewBox.baseVal;
    
    // Calculate the mouse position relative to the SVG element
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    
    // Convert to SVG coordinates accounting for viewBox
    let svgX = (mouseX / rect.width) * viewBox.width + viewBox.x;
    let svgY = (mouseY / rect.height) * viewBox.height + viewBox.y;

    // Account for the group transform (translate + scale)
    svgX = (svgX - position.x) / scale;
    svgY = (svgY - position.y) / scale;

    return { x: Math.round(svgX), y: Math.round(svgY) };
  }, [svgRef, position.x, position.y, scale]);

  // Find snap targets (vertices and edges) for the current cursor position
  const findSnapTarget = useCallback((cursorPos: { x: number; y: number }, excludeUnitId?: number, excludeNodeIndex?: number): { x: number; y: number } | null => {
    // Much tighter snap distance so users can draw finer lines.
    const snapDistance = Math.max(4, Math.min(10, 8 / scale));
    let bestSnapTarget: { x: number; y: number } | null = null;
    let bestDistance = snapDistance;

    // Check all units for snap targets
    units.forEach(unit => {
      if (unit.id === excludeUnitId) return; // Don't snap to the same polygon we're editing
      
      const unitPoints = pendingPolygonChanges.get(unit.id) || unit.points;
      if (!unitPoints || unitPoints.length < 2) return;

      // Snap to vertices
      unitPoints.forEach((point, index) => {
        if (unit.id === excludeUnitId && index === excludeNodeIndex) return; // Don't snap to the node we're dragging
        
        const distance = Math.sqrt(
          Math.pow(cursorPos.x - point[0], 2) + Math.pow(cursorPos.y - point[1], 2)
        );
        
        if (distance < bestDistance) {
          bestDistance = distance;
          bestSnapTarget = { x: point[0], y: point[1] };
        }
      });

      // Snap to edges (line segments)
      for (let i = 0; i < unitPoints.length; i++) {
        const p1 = unitPoints[i];
        const p2 = unitPoints[(i + 1) % unitPoints.length];
        
        // Calculate distance from cursor to line segment
        const lineLength = Math.sqrt(Math.pow(p2[0] - p1[0], 2) + Math.pow(p2[1] - p1[1], 2));
        if (lineLength === 0) continue;
        
        const t = Math.max(0, Math.min(1, 
          ((cursorPos.x - p1[0]) * (p2[0] - p1[0]) + (cursorPos.y - p1[1]) * (p2[1] - p1[1])) / (lineLength * lineLength)
        ));
        
        const projectionX = p1[0] + t * (p2[0] - p1[0]);
        const projectionY = p1[1] + t * (p2[1] - p1[1]);
        
        const distanceToEdge = Math.sqrt(
          Math.pow(cursorPos.x - projectionX, 2) + Math.pow(cursorPos.y - projectionY, 2)
        );
        
        if (distanceToEdge < bestDistance) {
          bestDistance = distanceToEdge;
          bestSnapTarget = { x: Math.round(projectionX), y: Math.round(projectionY) };
        }
      }
    });

    return bestSnapTarget;
  }, [units, pendingPolygonChanges, scale]);

  // Handle polygon selection for editing
  const handlePolygonEditClick = useCallback((unitId: number, event: React.MouseEvent) => {
    if (!polygonEditingMode) return;
    
    event.stopPropagation();
    setEditingPolygonId(editingPolygonId === unitId ? null : unitId);
  }, [polygonEditingMode, editingPolygonId]);

  // Handle node drag start
  const handleNodeDragStart = useCallback((event: React.MouseEvent, nodeIndex: number) => {
    if (!polygonEditingMode) return;
    
    event.stopPropagation();
    event.preventDefault();
    setDraggingNodeIndex(nodeIndex);
  }, [polygonEditingMode, getSVGCoordinates]);

  // Handle node drag with snapping
  const handleNodeDrag = useCallback((event: React.MouseEvent | MouseEvent) => {
    if (draggingNodeIndex === null || editingPolygonId === null) return;

    const currentCoords = getSVGCoordinates(event);
    const unit = units.find(u => u.id === editingPolygonId);
    
    if (!unit) return;

    // Check for snap targets
    const snapTarget = findSnapTarget(currentCoords, editingPolygonId, draggingNodeIndex);
    
    let finalCoords = currentCoords;
    if (snapTarget) {
      finalCoords = snapTarget;
      setSnapPosition(snapTarget);
      setShowSnapIndicator(true);
    } else {
      setShowSnapIndicator(false);
    }

    // Use pending changes if available, otherwise use unit points
    const currentPoints = pendingPolygonChanges.get(editingPolygonId) || unit.points;
    if (!currentPoints) return;

    const newPoints = [...currentPoints];
    newPoints[draggingNodeIndex] = [finalCoords.x, finalCoords.y];
    
    // Update the polygon coordinates
    onPolygonUpdate?.(editingPolygonId, newPoints);
  }, [draggingNodeIndex, editingPolygonId, getSVGCoordinates, units, onPolygonUpdate, pendingPolygonChanges, findSnapTarget]);

  // Handle node drag end
  const handleNodeDragEnd = useCallback(() => {
    setDraggingNodeIndex(null);
    setShowSnapIndicator(false);
    setSnapPosition(null);
  }, []);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !onWheelZoom) return;

    const handleWheel = (e: WheelEvent) => {
      onWheelZoom(e as unknown as React.WheelEvent);
    };

    svg.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      svg.removeEventListener('wheel', handleWheel);
    };
  }, [svgRef, onWheelZoom]);

  // Handle polygon node dragging
  useEffect(() => {
    if (draggingNodeIndex === null) return;

    const handleMouseMove = (event: MouseEvent) => {
      event.preventDefault();
      handleNodeDrag(event);
    };

    const handleMouseUp = () => {
      handleNodeDragEnd();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingNodeIndex, handleNodeDrag, handleNodeDragEnd]);

  // Handle ESC key to exit polygon editing mode
  useEffect(() => {
    if (!polygonEditingMode) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (draggingNodeIndex !== null) {
          // If currently dragging, stop the drag
          handleNodeDragEnd();
        } else if (editingPolygonId !== null) {
          // If a polygon is selected, deselect it
          setEditingPolygonId(null);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [polygonEditingMode, draggingNodeIndex, editingPolygonId, handleNodeDragEnd]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const handleTouchStartEvent = (e: TouchEvent) => {
      if (handleTouchStart) {
        handleTouchStart(e as unknown as React.TouchEvent);
      }
    };

    const handleTouchMoveEvent = (e: TouchEvent) => {
      if (handleTouchMove) {
        handleTouchMove(e as unknown as React.TouchEvent);
      }
    };

    const handleTouchEndEvent = (e: TouchEvent) => {
      if (handleTouchEnd) {
        handleTouchEnd(e as unknown as React.TouchEvent);
      }
    };

    svg.addEventListener('touchstart', handleTouchStartEvent, { passive: false });
    svg.addEventListener('touchmove', handleTouchMoveEvent, { passive: false });
    svg.addEventListener('touchend', handleTouchEndEvent, { passive: false });

    return () => {
      svg.removeEventListener('touchstart', handleTouchStartEvent);
      svg.removeEventListener('touchmove', handleTouchMoveEvent);
      svg.removeEventListener('touchend', handleTouchEndEvent);
    };
  }, [svgRef, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return (
    <div className="w-full h-full relative overflow-hidden">
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ cursor: getCursor(), touchAction: 'none' }}
      >
        <svg
          ref={svgRef}
          viewBox={`-40 -40 ${imageDimensions.width + 80} ${imageDimensions.height + 80}`}
          onMouseDown={handleMouseDown}
          onClick={handleSvgClick}
          preserveAspectRatio="xMidYMid meet"
          className="w-full h-full"
        >
          <g ref={groupRef} transform={`translate(${position.x}, ${position.y}) scale(${scale})`}>
            {/* Floor plan image */}
            {imagePath && (
              <image
                href={imagePath}
                width={imageDimensions.width}
                height={imageDimensions.height}
                x="0"
                y="0"
              />
            )}

            {/* Area polygons */}
            {units
              .filter((unit) => unit && Array.isArray(unit.points) && unit.points.length >= 3)
              .map((unit) => {
                const isSelected = selectedUnit?.id === unit.id;
                const areaName = Object.keys(unit.images || {})[0] || `Area ${unit.id}`;

                // Use pending polygon changes if available, otherwise use unit points
                const currentPoints = pendingPolygonChanges.get(unit.id) || unit.points;
                const validPoints = normalizePolygonPoints(currentPoints);

                if (validPoints.length < 3) return null; // Skip invalid polygons

                // Calculate proper polygon centroid (visual center)
                const center = calculatePolygonCentroid(validPoints);
                const xs = validPoints.map((p) => p[0]);
                const minX = Math.min(...xs);
                const maxX = Math.max(...xs);
                const maxLabelWidth = Math.max(40, maxX - minX - 16);
                const labelFontSize = 28;
                const maxLines = 4;
                const approxCharWidth = labelFontSize * 0.58;
                const maxCharsPerLine = Math.max(6, Math.floor(maxLabelWidth / approxCharWidth));

                // Get pending changes for this unit
                const pendingChanges = pendingTextChanges.get(unit.id) || {};
                
                // Use pending values if available, otherwise use unit values
                const currentName = pendingChanges.name || areaName;
                const currentTextPosition = pendingChanges.text_position || unit.text_position;
                const currentTextRotation = pendingChanges.text_rotation || unit.text_rotation;
                const wrappedLabelLines = wrapTextLines(currentName, maxCharsPerLine, maxLines);
                const lineHeight = labelFontSize * 1.15;
                const textCenterY = currentTextPosition && Array.isArray(currentTextPosition) && currentTextPosition.length === 2
                  ? currentTextPosition[1]
                  : center.y;
                const textCenterX = currentTextPosition && Array.isArray(currentTextPosition) && currentTextPosition.length === 2
                  ? currentTextPosition[0]
                  : center.x;
                const startY = textCenterY - ((wrappedLabelLines.length - 1) * lineHeight) / 2;
                return (
                  <g key={unit.id}>
                    <polygon
                      className={`unit-polygon ${isSelected ? 'selected' : ''}`}
                      data-unit-id={unit.id}
                      points={validPoints.map((point) => point.join(',')).join(' ')}
                      style={{
                        fill: isSelected ? 'rgba(96, 165, 250, 0.18)' : 'rgba(147, 197, 253, 0.08)',
                        stroke: isSelected ? '#2563eb' : '#60a5fa',
                        strokeWidth: isSelected ? 2 : 2,
                        strokeLinejoin: 'round',
                        strokeLinecap: 'round',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          (e.target as SVGPolygonElement).style.fill = 'rgba(96, 165, 250, 0.36)';
                          (e.target as SVGPolygonElement).style.stroke = '#3b82f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          (e.target as SVGPolygonElement).style.fill = 'rgba(147, 197, 253, 0.26)';
                          (e.target as SVGPolygonElement).style.stroke = '#60a5fa';
                        }
                      }}
                      onClick={(e) => {
                        if (polygonEditingMode) {
                          handlePolygonEditClick(unit.id, e);
                        } else if (textEditingMode) {
                          e.stopPropagation();
                          onRequestLabelEdit?.(unit.id);
                        } else {
                          handleUnitClick(unit, e);
                        }
                                             }}
                     />

                    {!isSelected ? (
                      <text
                        className="unit-label-text"
                        x={textCenterX}
                        y={startY}
                        textAnchor="middle"
                        dominantBaseline="hanging"
                        transform={currentTextRotation ? `rotate(${currentTextRotation} ${
                          textCenterX
                        } ${
                          textCenterY
                        })` : undefined}
                        style={{
                          font: `600 ${labelFontSize}px/1.25 "Inter", Helvetica, Arial, serif`,
                          fill: '#1f2937',
                          textShadow: '0 1px 2px rgba(255,255,255,1)',
                          pointerEvents: 'none',
                        }}
                                              >
                          {wrappedLabelLines.map((line, lineIndex) => (
                            <tspan
                              key={`${unit.id}-line-${lineIndex}`}
                              x={textCenterX}
                              dy={lineIndex === 0 ? 0 : lineHeight}
                            >
                              {line}
                            </tspan>
                          ))}
                        </text>
                    ) : null}

                  </g>
                );
              })
              .filter(Boolean)}

            {/* Current measurement polygon */}
            {currentPolygon.length > 0 && (
              <g>
                <polygon
                  className="polygon active"
                  points={currentPolygon.map((point) => point.join(',')).join(' ')}
                  style={{
                    fill: 'rgba(100, 149, 255, 0.306)',
                    stroke: '#4477ff',
                    strokeWidth: 2,
                  }}
                />
                {currentPolygon.map((point, index) => (
                  <g key={index}>
                    <circle
                      className="point-marker"
                      cx={point[0]}
                      cy={point[1]}
                      r="3"
                      style={{
                        fill: '#4477ff',
                        stroke: 'white',
                        strokeWidth: 2,
                      }}
                    />
                    <text
                      className="point-label"
                      x={point[0] + 5}
                      y={point[1] - 5}
                      style={{
                        fontSize: '12px',
                        fill: 'black',
                        fontFamily: 'Arial, sans-serif',
                      }}
                    >
                      {index + 1}
                    </text>
                  </g>
                ))}
              </g>
            )}

            {/* Photo markers */}
            {photoMarkers.map((marker) => {
              const isSelected = selectedMarkers.has(marker.id);
              const isVisible =
                !markersInAreas.has(marker) ||
                (selectedUnit &&
                  areaPhotoMap
                    .get(selectedUnit.id)
                    ?.some((photoId) => marker.photos.some((photo) => photo.id === photoId)));

              if (!isVisible && !photoIndexMode) return null;

              return (
                <g
                  key={marker.id}
                  className={`photo-marker ${photoIndexMode ? 'photo-index-active' : ''}`}
                  data-marker-number={marker.number}
                  data-marker-index={marker.id}
                  style={{ 
                    cursor: disableMarkerClicks ? 'default' : (photoIndexMode ? 'move' : 'pointer'), 
                    pointerEvents: 'auto',
                    opacity: disableMarkerClicks ? 0.7 : 1
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!disableMarkerClicks) {
                      if (!photoIndexMode) {
                        trackEvent('photo_marker', 'photo_marker', 'Property Documentation', {
                          layout_id: selectedLayoutId,
                          marker_number: marker.number,
                        });
                      }
                      handleMarkerClick(marker, e);
                    }
                  }}
                  onMouseDown={(e) => {
                    // Don't interfere with clicks when modifier keys are pressed
                    const hasModifier = e.shiftKey || e.ctrlKey || e.metaKey;
                    
                    if (photoIndexMode && !hasModifier && e.button === 0) {
                      e.stopPropagation();
                      
                      // Track mousedown position to detect actual drag vs click
                      const startX = e.clientX;
                      const startY = e.clientY;
                      let hasMoved = false;
                      
                      const handleMouseMove = (moveEvent: MouseEvent) => {
                        const deltaX = Math.abs(moveEvent.clientX - startX);
                        const deltaY = Math.abs(moveEvent.clientY - startY);
                        
                        // Only start drag if moved more than 3 pixels
                        if (deltaX > 3 || deltaY > 3) {
                          if (!hasMoved && handleMarkerDragStart) {
                            hasMoved = true;
                            handleMarkerDragStart(marker.id, e);
                          }
                        }
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }
                  }}
                  onContextMenu={(e) => {
                    if (photoIndexMode && handleMarkerRightClick) {
                      handleMarkerRightClick(e);
                    }
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  onTouchEnd={(e) => {
                    if (!disableMarkerClicks) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleMarkerClick(marker, e as unknown as React.MouseEvent);
                    }
                  }}
                >
                  {/* Tooltip for markers with no photos */}
                  {!photoIndexMode && marker.photos.length === 0 && (
                    <title>No photos available at this location yet</title>
                  )}
                  <rect
                    x={marker.x - marker.width / 2}
                    y={marker.y - marker.height / 2}
                    width={marker.width}
                    height={marker.height}
                    fill={
                      isSelected
                        ? '#4477ff'
                        : marker.photos.some((photo) => photo.scan_type === 'Finished')
                          ? '#4477ff'
                          : 'red'
                    }
                    stroke={isSelected ? '#1e40af' : 'black'}
                    strokeWidth={isSelected ? '2' : '1'}
                    rx="5"
                    ry="5"
                    style={{ pointerEvents: 'auto' }}
                  />
                  <text
                    x={marker.x}
                    y={marker.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="white"
                    fontSize={marker.fontSize}
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  >
                    {marker.number}
                  </text>
                  {photoIndexMode && (
                    <g
                      className="delete-button"
                      style={{ opacity: 0 }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as SVGGElement).style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as SVGGElement).style.opacity = '0';
                      }}
                    >
                      <circle
                        cx={marker.x + marker.width / 2}
                        cy={marker.y - marker.height / 2}
                        r="8"
                        fill="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          deletePhotoMarker(marker.id);
                        }}
                      />
                      <text
                        x={marker.x + marker.width / 2}
                        y={marker.y - marker.height / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="white"
                        fontSize="10"
                        style={{
                          pointerEvents: 'none',
                          fontWeight: 'bold',
                        }}
                      >
                        ×
                      </text>
                    </g>
                  )}
                </g>
              );
            })}

          {/* Polygon editing nodes - Rendered on top of everything else */}
          {polygonEditingMode && editingPolygonId !== null && (() => {
            const editingUnit = units.find(u => u.id === editingPolygonId);
            if (!editingUnit) return null;
            
            const validPoints = pendingPolygonChanges.get(editingPolygonId) || editingUnit.points;
            if (!validPoints || validPoints.length === 0) return null;
            
            return (
              <>
                {validPoints.map((point, index) => (
                  <g key={`editing-node-${index}`}>
                    {/* Node background (larger, for easier clicking) */}
                    <circle
                      cx={point[0]}
                      cy={point[1]}
                      r="10"
                      fill="transparent"
                      className="cursor-move"
                      onMouseDown={(e) => handleNodeDragStart(e, index)}
                    />
                    {/* Visible node */}
                    <circle
                      cx={point[0]}
                      cy={point[1]}
                      r="6"
                      fill={draggingNodeIndex === index ? "#1d4ed8" : "#3b82f6"}
                      stroke="white"
                      strokeWidth="2"
                      className="cursor-move"
                      style={{
                        opacity: draggingNodeIndex === index ? 0.8 : 1,
                        transform: draggingNodeIndex === index ? 'scale(1.3)' : 'scale(1)',
                        transition: draggingNodeIndex === index ? 'none' : 'transform 0.1s ease',
                        pointerEvents: 'none', // Let the background circle handle events
                      }}
                    />
                    {/* Node number for reference */}
                    <text
                      x={point[0]}
                      y={point[1]}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="white"
                      fontSize="8"
                      fontWeight="bold"
                      style={{ pointerEvents: 'none' }}
                    >
                      {index + 1}
                    </text>
                  </g>
                ))}
              </>
            );
          })()}

          {/* Snap indicator - Only show when snapping during polygon editing */}
          {showSnapIndicator && snapPosition && (
            <circle
              cx={snapPosition.x}
              cy={snapPosition.y}
              r="8"
              fill="none"
              stroke="#f59e0b"
              strokeWidth="3"
              strokeDasharray="4,4"
              className="animate-pulse"
              style={{ pointerEvents: 'none' }}
            />
          )}

          {/* Area delete controls (rendered above markers) */}
          {showAreaDeleteControls &&
            units
              .filter((unit) => unit && Array.isArray(unit.points) && unit.points.length >= 3)
              .map((unit) => {
                const currentPoints = pendingPolygonChanges.get(unit.id) || unit.points;
                const validPoints = normalizePolygonPoints(currentPoints);
                if (validPoints.length < 3) return null;

                const center = calculatePolygonCentroid(validPoints);
                const pendingChanges = pendingTextChanges.get(unit.id) || {};
                const currentTextPosition = pendingChanges.text_position || unit.text_position;
                const textCenterY =
                  currentTextPosition && Array.isArray(currentTextPosition) && currentTextPosition.length === 2
                    ? currentTextPosition[1]
                    : center.y;
                const textCenterX =
                  currentTextPosition && Array.isArray(currentTextPosition) && currentTextPosition.length === 2
                    ? currentTextPosition[0]
                    : center.x;

                const deleteButtonX = textCenterX;
                const deleteButtonY = textCenterY - 26;

                return (
                  <g
                    key={`delete-area-${unit.id}`}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteAreaTag?.(unit.id);
                    }}
                  >
                    <circle cx={deleteButtonX} cy={deleteButtonY} r="11" fill="#dc2626" />
                    <rect
                      x={deleteButtonX - 3.5}
                      y={deleteButtonY - 1.5}
                      width={7}
                      height={7}
                      rx={1}
                      fill="none"
                      stroke="#ffffff"
                      strokeWidth={1}
                      pointerEvents="none"
                    />
                    <line
                      x1={deleteButtonX - 4.5}
                      y1={deleteButtonY - 1.5}
                      x2={deleteButtonX + 4.5}
                      y2={deleteButtonY - 1.5}
                      stroke="#ffffff"
                      strokeWidth={1}
                      pointerEvents="none"
                    />
                    <line
                      x1={deleteButtonX - 2}
                      y1={deleteButtonY - 3.5}
                      x2={deleteButtonX + 2}
                      y2={deleteButtonY - 3.5}
                      stroke="#ffffff"
                      strokeWidth={1}
                      pointerEvents="none"
                    />
                    <line
                      x1={deleteButtonX - 1.2}
                      y1={deleteButtonY + 1}
                      x2={deleteButtonX - 1.2}
                      y2={deleteButtonY + 4.5}
                      stroke="#ffffff"
                      strokeWidth={0.8}
                      pointerEvents="none"
                    />
                    <line
                      x1={deleteButtonX + 1.2}
                      y1={deleteButtonY + 1}
                      x2={deleteButtonX + 1.2}
                      y2={deleteButtonY + 4.5}
                      stroke="#ffffff"
                      strokeWidth={0.8}
                      pointerEvents="none"
                    />
                  </g>
                );
              })}

          {/* Selected area chip label (rendered last so it's always on top) */}
          {!textEditingMode && selectedUnit && (() => {
            const selected = units.find((u) => u.id === selectedUnit.id);
            if (!selected) return null;

            const selectedPointsRaw = pendingPolygonChanges.get(selected.id) || selected.points;
            const selectedPoints = normalizePolygonPoints(selectedPointsRaw);
            if (selectedPoints.length < 3) return null;

            const center = calculatePolygonCentroid(selectedPoints);
            const minY = Math.min(...selectedPoints.map((p) => p[1]));
            const areaName = Object.keys(selected.images || {})[0] || `Area ${selected.id}`;
            const pendingChanges = pendingTextChanges.get(selected.id) || {};
            const currentName = pendingChanges.name || areaName;
            const chipLines = wrapTextLines(currentName, 22, 2);
            const chipLineHeight = 16;
            const chipPaddingX = 10;
            const longestLine = chipLines.reduce((m, l) => Math.max(m, l.length), 0);
            const chipWidth = Math.max(80, longestLine * 8 + chipPaddingX * 2);
            const chipHeight = chipLines.length * chipLineHeight + 10;
            const chipCenterX = center.x;
            const chipCenterY = Math.max(8 + chipHeight / 2, minY - (chipHeight / 2) - 8);

            return (
              <g style={{ transition: 'all 180ms ease' }}>
                <rect
                  x={chipCenterX - chipWidth / 2}
                  y={chipCenterY - chipHeight / 2}
                  width={chipWidth}
                  height={chipHeight}
                  rx={10}
                  ry={10}
                  fill="#1d4ed8"
                  opacity={0.95}
                />
                <text
                  x={chipCenterX}
                  y={chipCenterY - ((chipLines.length - 1) * chipLineHeight) / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{
                    font: '600 14px/1.2 "Inter", Helvetica, Arial, serif',
                    fill: '#ffffff',
                    pointerEvents: 'none',
                  }}
                >
                  {chipLines.map((line, lineIndex) => (
                    <tspan
                      key={`${selected.id}-selected-chip-line-${lineIndex}`}
                      x={chipCenterX}
                      dy={lineIndex === 0 ? 0 : chipLineHeight}
                    >
                      {line}
                    </tspan>
                  ))}
                </text>
              </g>
            );
          })()}
          </g>
        </svg>
      </div>

      {/* Editing Save/Cancel Panel */}
      {polygonEditingMode && pendingPolygonChanges.size > 0 && (
        <Card className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground">
                <span className="font-medium">{pendingPolygonChanges.size}</span> unsaved polygon change{pendingPolygonChanges.size !== 1 ? 's' : ''}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={onCancelTextChanges}>
                  Cancel
                </Button>
                <Button variant="default" size="sm" onClick={onSaveTextChanges}>
                  Save Changes
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coordinates Panel */}
      {showCoordinates && (
        <Card className="absolute bottom-20 right-4 w-80 max-h-96 overflow-y-auto">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex justify-between items-center">
              Polygon Coordinates
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(currentPolygon));
                  alert('Coordinates copied to clipboard!');
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-3 rounded-md whitespace-pre-wrap">
              {JSON.stringify(currentPolygon, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MainContentArea;
